import { alpha, Theme } from '@mui/material/styles';
import { TableContainerProps } from '@mui/material/TableContainer';
import { tableRowClasses } from '@mui/material/TableRow';

import { dashedRing } from '../../css';

export function table(theme: Theme) {
  const rootStyles = (ownerState: TableContainerProps) => {
    const detailsVariant = ownerState.variant === 'details';
    const detailsWithSummaryVariant = ownerState.variant === 'detailsWithSummary';
    const detailsWithTotalsVariant = ownerState.variant === 'detailsWithTotals';
    const borderedVariant = ownerState.variant === 'bordered';
    const isDetailsLike = detailsVariant || detailsWithSummaryVariant || detailsWithTotalsVariant;

    const ring = dashedRing(theme);
    const { border: dashedBorder, borderRadius: cornerRadius } = ring;

    return {
      ...(isDetailsLike && {
        '& .MuiTableHead-root': {
          '& .MuiTableCell-root': {
            height: '50px',
            fontSize: 14,
            // The generous left indent is meant for the first data column. A checkbox column,
            // when present, must keep its own compact padding so header/body checkboxes line up.
            '&:first-of-type:not(.MuiTableCell-paddingCheckbox)': {
              paddingLeft: theme.spacing(5.25),
            },
            '&:last-of-type': {
              paddingRight: theme.spacing(2),
            },
          },
        },
        '& .MuiTableBody-root': {
          '& .MuiTableCell-root': {
            height: '50px',
            // `height` acts as a min-height here, so single-line rows still measure 50px; the
            // padding only shows on taller content, which would otherwise touch the row edges.
            paddingTop: theme.spacing(1),
            paddingBottom: theme.spacing(1),
            border: 'none',
            fontSize: 14,
            '&:first-of-type:not(.MuiTableCell-paddingCheckbox)': {
              paddingLeft: theme.spacing(5.25),
            },
            '&:last-of-type': {
              paddingRight: theme.spacing(2),
            },
          },
          '& .MuiTableCell-root:only-child': {
            height: 'auto',
            padding: theme.spacing(1),
          },
        },
      }),
      ...(detailsVariant && ring),
      ...(borderedVariant && {
        ...ring,
        overflow: 'hidden',
        '& .MuiTable-root': {
          borderCollapse: 'separate',
          borderSpacing: 0,
        },
        '& .MuiTableHead-root .MuiTableCell-root': {
          borderBottom: 'none',
        },
        '& .MuiTableBody-root .MuiTableCell-root': {
          border: 'none',
        },
      }),
      ...(detailsWithSummaryVariant && {
        '& .MuiTableHead-root .MuiTableCell-root': {
          borderTop: dashedBorder,
          '&:first-of-type': {
            borderLeft: dashedBorder,
            borderTopLeftRadius: cornerRadius,
          },
          '&:last-of-type': {
            borderRight: dashedBorder,
            borderTopRightRadius: cornerRadius,
          },
        },
        '& .MuiTableBody-root': {
          '& .MuiTableRow-root .MuiTableCell-root': {
            '&:first-of-type': {
              borderLeft: dashedBorder,
            },
            '&:last-of-type': {
              borderRight: dashedBorder,
            },
          },
          '& .MuiTableRow-root:nth-last-of-type(2) .MuiTableCell-root': {
            borderBottom: dashedBorder,
            '&:first-of-type': {
              borderBottomLeftRadius: cornerRadius,
            },
          },
          '& .MuiTableRow-root:last-of-type': {
            '& .MuiTableCell-root': {
              borderBottom: dashedBorder,
            },
            '& .MuiTableCell-root:first-of-type': {
              border: 'none',
            },
            '& .MuiTableCell-root:nth-of-type(2)': {
              borderLeft: dashedBorder,
              borderBottomLeftRadius: cornerRadius,
              backgroundColor: theme.palette.background.neutral,
              color: theme.palette.text.secondary,
              fontWeight: theme.typography.fontWeightSemiBold,
              textAlign: 'right',
              whiteSpace: 'nowrap',
            },
            '& .MuiTableCell-root:last-of-type': {
              borderRight: dashedBorder,
              borderLeft: 'none',
              borderBottomRightRadius: cornerRadius,
            },
          },
        },
      }),
      ...(detailsWithTotalsVariant && {
        '& .MuiTableHead-root .MuiTableCell-root': {
          borderTop: dashedBorder,
          '&:first-of-type': {
            borderLeft: dashedBorder,
            borderTopLeftRadius: cornerRadius,
          },
          '&:last-of-type': {
            borderRight: dashedBorder,
            borderTopRightRadius: cornerRadius,
          },
        },
        '& .MuiTableBody-root': {
          '& .MuiTableRow-root .MuiTableCell-root': {
            '&:first-of-type': {
              borderLeft: dashedBorder,
            },
            '&:last-of-type': {
              borderRight: dashedBorder,
            },
          },
          // The totals row spans every column so each figure sits under its own header, but only
          // the label cell is tinted — the figures stay on the table's own ground, as in the design.
          '& .MuiTableRow-root:last-of-type .MuiTableCell-root': {
            borderTop: dashedBorder,
            borderBottom: dashedBorder,
            fontWeight: theme.typography.fontWeightSemiBold,
            whiteSpace: 'nowrap',
            '&:first-of-type': {
              backgroundColor: theme.palette.background.neutral,
              color: theme.palette.text.secondary,
              borderBottomLeftRadius: cornerRadius,
            },
            '&:last-of-type': {
              borderBottomRightRadius: cornerRadius,
            },
          },
        },
      }),
    };
  };

  return {
    MuiTableContainer: {
      styleOverrides: {
        root: ({ ownerState }: { ownerState: TableContainerProps }) => ({
          position: 'relative',
          ...rootStyles(ownerState),
        }),
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          [`&.${tableRowClasses.selected}`]: {
            backgroundColor: alpha(theme.palette.primary.dark, 0.04),
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.dark, 0.08),
            },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomStyle: 'dashed',
        },
        head: {
          fontSize: 14,
          color: theme.palette.text.secondary,
          fontWeight: theme.typography.fontWeightSemiBold,
          backgroundColor: theme.palette.background.neutral,
        },
        stickyHeader: {
          backgroundColor: theme.palette.background.paper,
          backgroundImage: `linear-gradient(to bottom, ${theme.palette.background.neutral} 0%, ${theme.palette.background.neutral} 100%)`,
        },
        paddingCheckbox: {
          paddingLeft: theme.spacing(1),
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          width: '100%',
        },
        toolbar: {
          height: 64,
        },
        actions: {
          marginRight: 8,
        },
        select: {
          paddingLeft: 8,
          '&:focus': {
            borderRadius: theme.shape.borderRadius,
          },
        },
        selectIcon: {
          right: 4,
          width: 16,
          height: 16,
          top: 'calc(50% - 8px)',
        },
      },
    },
  };
}
